import { useCallback, useMemo } from 'react';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Box } from '@givebutter/primitives/box';
import { Separator } from '@givebutter/primitives/separator';
import * as Toast from '@givebutter/primitives/toast';
import { pluralize } from '@givebutter/util';
import {
    faExclamationCircle,
    faPhone,
    faSpinnerThird,
    faXmarkToSlot,
} from '@fortawesome/pro-regular-svg-icons';
import { faSoap } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    BulkIgnoreDataHygienePhoneNumbersParams,
    bulkIgnoreDataHygienePhoneNumbers,
    fetchDataHygienePhoneNumbers,
    undoBulkIgnoreDataHygienePhoneNumbers,
} from 'api/contacts/data-hygiene';
import { CountBadge } from 'assets/js/pages/contacts/data-hygiene/CountBadge';
import { DataHygienePhoneNumberContext } from 'assets/js/pages/contacts/data-hygiene/DataHygienePhoneNumberContext';
import * as EducationalContentDialog from 'assets/js/pages/contacts/data-hygiene/EducationalContentDialog';
import { HasSavedTableEmpty } from 'assets/js/pages/contacts/data-hygiene/HasSavedTableEmpty';
import * as IgnoreButton from 'assets/js/pages/contacts/data-hygiene/IgnoreButton';
import { NameTableCell } from 'assets/js/pages/contacts/data-hygiene/NameTableCell';
import { PhoneNumberStatus } from 'assets/js/pages/contacts/data-hygiene/PhoneNumberStatus';
import { PhoneNumberUpdate } from 'assets/js/pages/contacts/data-hygiene/PhoneNumberUpdate';
import { PhoneNumbersTrialProgressCard } from 'assets/js/pages/contacts/data-hygiene/PhoneNumbersTrialProgressCard';
import { ReviewingYourDataTableEmpty } from 'assets/js/pages/contacts/data-hygiene/ReviewingYourDataTableEmpty';
import { SavedForLaterTableEmpty } from 'assets/js/pages/contacts/data-hygiene/SavedForLaterTableEmpty';
import { ToggleSaveForLaterButton } from 'assets/js/pages/contacts/data-hygiene/ToggleSaveForLaterButton';
import { HYGIENE_PAGE_SIZES } from 'assets/js/pages/contacts/data-hygiene/constants';
import { useToggleSavedForLater } from 'assets/js/pages/contacts/data-hygiene/hooks/useToggleSavedForLater';
import { FilterGroupProvider } from 'components/filter/filters-next/FilterGroupContext';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { QuickFiltersBar } from 'components/filter/filters-next/QuickFiltersBar';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import { ServerKey } from 'components/filter/types';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { RowPendingFeature } from 'components/table/RowPendingFeature';
import { RowReplacementFeature } from 'components/table/RowReplacementFeature';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useDataHygieneMetrics } from 'customHooks/useDataHygieneMetrics';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { DataHygienePhoneNumber } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const columnHelper = createColumnHelper<DataHygienePhoneNumber>();
const allFilters: ServerKey[] = ['invalidPhoneNumber', 'bouncedPhoneNumber', 'landlinePhoneNumber'];

const DataHygienePhoneNumbers = () => {
    const [search, setSearch] = usePersistentSearch();
    const debouncedSearch = useDebounce(search);
    const { accountId, dataHygiene, isCurrentlyOnFreeTrial } = useCurrentAccount();
    const { success: toastSuccess, error: toastError } = Toast.useToast();
    const { data: metrics } = useDataHygieneMetrics();
    const { savedForLater, toggleSavedForLater } = useToggleSavedForLater();
    const { filters } = useFiltersByKey('dataHygienePhoneNumbers');

    const {
        pagination,
        setPagination,
        rowPending,
        setRowPending,
        rowReplacement,
        setRowReplacement,
    } = Table.useReactTableState({
        pageSize: HYGIENE_PAGE_SIZES[1],
    });

    const [isPending, params] = useIsPending(
        {
            query: debouncedSearch || undefined,
            page: pagination.pageIndex + 1,
            per_page: pagination.pageSize,
            eager: true,
            filters,
            options: { saved_for_later: savedForLater },
        },
        500,
    );

    const { data: phoneNumbers, isFetching } = useSuspenseQuery({
        queryKey: accountQueryKeys.dataHygienePhoneNumbersWithFilters(accountId, params),
        queryFn: () => fetchDataHygienePhoneNumbers(accountId, params),
    });

    const { mutate: undoBulkIgnore } = useMutation({
        mutationFn: (values: BulkIgnoreDataHygienePhoneNumbersParams) =>
            undoBulkIgnoreDataHygienePhoneNumbers(accountId, {
                ...values,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygienePhoneNumbers(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toastError(error?.response?.data?.message ?? 'Failed to undo ignore');
        },
    });

    const { mutate: bulkIgnore, isPending: bulkIgnoreIsPending } = useMutation({
        mutationFn: (values: BulkIgnoreDataHygienePhoneNumbersParams) =>
            bulkIgnoreDataHygienePhoneNumbers(accountId, {
                ...values,
            }),
        onMutate: values => {
            if (table.options.enableRowPending) {
                table.addRowPending(values.row_ids);
            }

            // NOTE: return the values to be used in context for below handlers
            return { values };
        },
        onSuccess: (data, variables, context) => {
            const { values } = context ?? {};
            const issueCount = values?.ids?.length || 0;

            toastSuccess(
                <div className="flex flex-col">
                    <h2 className="font-medium text-gray-900">
                        {issueCount > 0 ? (
                            <span>
                                {issueCount} {pluralize(issueCount, 'issue')} ignored
                            </span>
                        ) : (
                            <span>Successfully ignored</span>
                        )}
                    </h2>
                    <div className="flex flex-col gap-2">
                        <span className="text-gray-700">We'll remind you again in 90 days.</span>
                        <div>
                            <Toast.Close
                                className="block text-sm font-medium text-blue-500 hover:underline"
                                onClick={() => {
                                    undoBulkIgnore(values);
                                }}
                            >
                                <span>Undo</span>
                            </Toast.Close>
                        </div>
                    </div>
                </div>,
                { duration: 5000, size: 'md' },
            );

            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygienePhoneNumbers(accountId),
            });
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.dataHygieneMetrics(accountId),
            });
        },
        onError: (error: GivebutterAxiosError) => {
            toastError(error?.response?.data?.message ?? 'An unknown error occurred');
        },
        onSettled: (data, error, variables, context) => {
            const { values } = context ?? {};

            if (table.options.enableRowPending && values?.row_ids) {
                table.removeRowPending(values.row_ids);
            }
        },
    });

    const columns = useMemo(
        () => [
            columnHelper.display({
                id: Table.Constants.Select,
                header: () => <Table.HeaderCheckbox />,
                cell: () => <Table.RowCheckbox />,
                meta: {
                    ignoreLink: true,
                    className: '!w-4',
                    headerClassName: '!w-4 !pr-0',
                },
            }),
            columnHelper.accessor('contact', {
                header: 'Name',
                meta: {
                    className: '!pl-3 [&>*]:whitespace-normal !w-1/4',
                    headerClassName: '!pl-3 !w-1/4',
                },
                cell: ({ getValue }) => (
                    <NameTableCell contact={getValue()} accountId={accountId} />
                ),
            }),
            columnHelper.accessor('deliverability_status', {
                header: 'Issue',
                meta: {
                    className: '[&>*]:whitespace-normal !w-1/4',
                    headerClassName: '!w-1/4',
                },
                cell: ({ getValue }) => <PhoneNumberStatus status={getValue()} />,
            }),
            columnHelper.display({
                id: 'Phone number',
                header: 'Phone number',
                meta: {
                    className: '[&>*]:whitespace-normal !w-2/4',
                    headerClassName: '!w-2/4',
                },
                cell: info => {
                    return <PhoneNumberUpdateCell phone_number={info.row.original} />;
                },
            }),
        ],
        [accountId],
    );

    const DerivedTableEmpty = useCallback(() => {
        if (savedForLater) {
            return <SavedForLaterTableEmpty className="max-w-md" />;
        } else if (metrics.data_hygiene.phone_numbers.saved_for_later > 0) {
            return (
                <HasSavedTableEmpty
                    className="max-w-md"
                    toggleSavedForLater={toggleSavedForLater}
                />
            );
        }

        if (
            isCurrentlyOnFreeTrial &&
            dataHygiene.free_trial_scans?.phone_numbers.deliverability.is_processing
        ) {
            return <ReviewingYourDataTableEmpty />;
        }

        return (
            <Table.Empty>
                <Table.EmptyIcon>
                    <FontAwesomeIcon icon={faSoap} />
                </Table.EmptyIcon>
                <Table.EmptyTitle>Looks clean!</Table.EmptyTitle>
                <Table.EmptyDescription>
                    All phone numbers attached to contacts don't require correction.
                </Table.EmptyDescription>
            </Table.Empty>
        );
    }, [metrics, savedForLater, toggleSavedForLater, dataHygiene, isCurrentlyOnFreeTrial]);

    const tableSearchTerm: string | undefined = debouncedSearch;
    const isFiltered = !isEmpty(filters);
    const tableData = phoneNumbers?.data ?? [];
    const phoneNumbersResultsCount = phoneNumbers.meta.total ?? 0;

    const table = useReactTable({
        _features: [RowPendingFeature, RowReplacementFeature],
        data: tableData,
        columns,
        pageCount: phoneNumbers.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            rowPending,
            rowReplacement,
        },
        onPaginationChange: updater => {
            setPagination(updater);
            table.resetRowSelection();
        },
        manualPagination: true,
        enableSorting: false,
        enableHiding: false,
        enableColumnPinning: false,
        getRowId: row => row.id.toString(),
        enableRowPending: true,
        onRowPendingChange: setRowPending,
        enableRowReplacement: true,
        onRowReplacementChange: setRowReplacement,
    });

    const selectedRows = table.getSelectedRowModel().flatRows;
    const selectedRowsOriginal = selectedRows.map(row => row.original);

    useSyncPaginatedData(table, phoneNumbers);

    return (
        <div className="flex flex-col gap-4">
            <PhoneNumbersTrialProgressCard />
            <div className="flex flex-col gap-1">
                <div className="flex flex-wrap justify-between gap-x-4">
                    <h1 className={'text-xl font-medium leading-snug'}>Phone numbers</h1>
                    <EducationalContentDialog.Root>
                        <EducationalContentDialog.Trigger>
                            About issues
                        </EducationalContentDialog.Trigger>
                        <EducationalContentDialog.Content>
                            <EducationalContentDialog.Header>
                                <EducationalContentDialog.Title>
                                    Types of phone number issues
                                </EducationalContentDialog.Title>
                                <p>Your data is separated into 3 types of issues:</p>
                            </EducationalContentDialog.Header>
                            <div className="flex flex-col gap-4">
                                <div className="flex gap-3 text-sm text-gray-600">
                                    <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-red-100 text-base text-red-600">
                                        <FontAwesomeIcon icon={faExclamationCircle} />
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="font-medium text-gray-700">
                                            Invalid phone number
                                        </h3>
                                        <p>
                                            This number is either not deliverable or it is a
                                            disposable number. It may not be deliverable because it
                                            includes wrong information or the phone number does not
                                            exist.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3 text-sm text-gray-600">
                                    <div className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-orange-100 text-base text-orange-600">
                                        <FontAwesomeIcon icon={faXmarkToSlot} />
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="font-medium text-gray-700">Bounced text</h3>
                                        <p>
                                            A bounced phone number is due to a text delivery attempt
                                            being blocked, sent to an incorrect number, or landline.
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-3 text-sm text-gray-600">
                                    <div className="bg-brand-violet-light text-brand-violet-primary inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-base">
                                        <FontAwesomeIcon icon={faPhone} />
                                    </div>
                                    <div className="flex flex-col gap-0.5">
                                        <h3 className="font-medium text-gray-700">
                                            Landline number
                                        </h3>
                                        <p>
                                            Landline numbers as primary phone numbers are flagged so
                                            that a mobile one can be added for text message
                                            communications.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </EducationalContentDialog.Content>
                    </EducationalContentDialog.Root>
                </div>
                <p className="text-sm text-gray-600">
                    Phone numbers are routinely checked for validity and bounces via text
                    communication. Any number recognized as invalid will appear in the table below
                    for correction. Additionally, primary phone numbers must be a mobile number in
                    order for text communications to be valid. Landline numbers are flagged so you
                    can quickly enter a mobile alternative for that contact.
                </p>
            </div>

            <FilterProvider
                cacheKey={'dataHygienePhoneNumbers'}
                resource={'dataHygienePhoneNumbers'}
            >
                <Box>
                    <div className="@container">
                        <div className="flex items-center justify-between p-3">
                            <div className="flex gap-3">
                                <Input.Root>
                                    <Input.SearchIcon />
                                    <Input.Form
                                        placeholder={'Search'}
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </Input.Root>
                                <ToggleSaveForLaterButton
                                    savedForLater={savedForLater}
                                    toggleSavedForLater={toggleSavedForLater}
                                    count={metrics.data_hygiene.phone_numbers.saved_for_later}
                                />
                            </div>

                            <Table.ResultsCount fallbackTotal={phoneNumbersResultsCount} />
                        </div>
                        <Separator />
                        <div
                            className={
                                '@2xl:justify-between flex flex-wrap items-center justify-end gap-4 px-3 py-2'
                            }
                        >
                            <div className={'@2xl:w-auto flex w-full flex-wrap items-center gap-2'}>
                                <FilterGroupProvider
                                    showInnerOperator={false}
                                    showOuterOperator={false}
                                    defaultOperator="or"
                                >
                                    <QuickFiltersBar quickFilters={allFilters} />
                                </FilterGroupProvider>
                            </div>

                            <IgnoreButton.Root>
                                <IgnoreButton.Button
                                    size="sm"
                                    disabled={
                                        !selectedRows?.length || bulkIgnoreIsPending || isFetching
                                    }
                                    onClick={() => {
                                        bulkIgnore({
                                            ids: selectedRowsOriginal.map(x => x.id),
                                            row_ids: selectedRows.map(x => x.id),
                                        });
                                    }}
                                >
                                    {bulkIgnoreIsPending ? (
                                        <>
                                            <FontAwesomeIcon
                                                className="animate-spin"
                                                icon={faSpinnerThird}
                                            />
                                            Ignoring
                                        </>
                                    ) : (
                                        <>
                                            <span>Ignore selected</span>
                                            {selectedRows?.length && !isFetching ? (
                                                <CountBadge count={selectedRows.length} size="sm" />
                                            ) : null}
                                        </>
                                    )}
                                </IgnoreButton.Button>
                                <IgnoreButton.AlertContent>
                                    <IgnoreButton.AlertBody>
                                        <IgnoreButton.Title />
                                        <IgnoreButton.Description />
                                    </IgnoreButton.AlertBody>
                                    <IgnoreButton.AlertAction
                                        onClick={() => {
                                            bulkIgnore({
                                                ids: selectedRowsOriginal.map(x => x.id),
                                                row_ids: selectedRows.map(x => x.id),
                                            });
                                        }}
                                    />
                                </IgnoreButton.AlertContent>
                            </IgnoreButton.Root>
                        </div>
                        <Separator />
                    </div>

                    <Table.Root
                        isLoading={isPending}
                        table={table}
                        searchTerm={tableSearchTerm}
                        isFiltered={isFiltered}
                        variant="bordered"
                    >
                        <Table.Content>
                            <Table.Header />
                            <Table.Body className="align-top [&>tr>td>*]:h-auto [&>tr>td]:h-auto [&>tr>td]:px-6 [&>tr>td]:py-4" />
                        </Table.Content>
                        <DerivedTableEmpty />
                        <Table.Pagination className={'border-t border-t-gray-100 p-2'}>
                            <Table.PaginationPageNavigation />
                            <Table.PaginationPerPageDropdown pageSizes={HYGIENE_PAGE_SIZES} />
                        </Table.Pagination>
                    </Table.Root>
                </Box>
            </FilterProvider>
        </div>
    );
};

const PhoneNumberUpdateCell = (props: { phone_number: DataHygienePhoneNumber }) => {
    const { phone_number } = props;
    const cellData = useMemo(() => ({ phone_number }), [phone_number]);

    return (
        <DataHygienePhoneNumberContext.Provider value={cellData}>
            <PhoneNumberUpdate />
        </DataHygienePhoneNumberContext.Provider>
    );
};

export { DataHygienePhoneNumbers };
